import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import cn from 'classnames';
import { useDevice } from '../../providers/Device';
import { useIsInBottomSheet } from '../../providers/IsInBottomSheetProvider';
import { BottomSheet } from '../BottomSheet/BottomSheet';
import { BottomSheetContentLayout } from '../BottomSheet/BottomSheetContentLayout/BottomSheetContentLayout';
import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { Typography } from '../Typography';
import styles from './ConfirmationModal.module.scss';
import { ConfirmationModalTestIds } from './ConfirmationModal.utils';
export function ConfirmationModal(props) {
    const { title, subtitle, content, body, className, contentClassName, bodyClassName, footerClassName, headerClassName, contentRef, skin = 'primary', cancelButtonDisabled, primaryButtonDisabled, primaryButtonLabel, primaryButtonTestId, primaryButtonTooltip, primaryButtonLoading, primaryButtonIntent, primaryButtonFormId, secondaryButtonLabel, secondaryButtonTestId, secondaryButtonLoading, secondaryButtonDisabled, width = 590, height, maxHeight, textAlignCenter = true, as = 'div', contentPadding = 'normal', overflowContent = false, onPrimaryButtonClick, onSecondaryButtonClick, onCloseButtonClick, openBottomSheetOnMobile, hideBottomSheetOnDismiss, testId, bi, noBi: _noBi, style, useStandardTextColors } = props, rest = _object_without_properties(props, [
        "title",
        "subtitle",
        "content",
        "body",
        "className",
        "contentClassName",
        "bodyClassName",
        "footerClassName",
        "headerClassName",
        "contentRef",
        "skin",
        "cancelButtonDisabled",
        "primaryButtonDisabled",
        "primaryButtonLabel",
        "primaryButtonTestId",
        "primaryButtonTooltip",
        "primaryButtonLoading",
        "primaryButtonIntent",
        "primaryButtonFormId",
        "secondaryButtonLabel",
        "secondaryButtonTestId",
        "secondaryButtonLoading",
        "secondaryButtonDisabled",
        "width",
        "height",
        "maxHeight",
        "textAlignCenter",
        "as",
        "contentPadding",
        "overflowContent",
        "onPrimaryButtonClick",
        "onSecondaryButtonClick",
        "onCloseButtonClick",
        "openBottomSheetOnMobile",
        "hideBottomSheetOnDismiss",
        "testId",
        "bi",
        "noBi",
        "style",
        "useStandardTextColors"
    ]);
    const [loading, setLoading] = useState(false);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const shouldHostInSheet = openBottomSheetOnMobile === true && isMobileDevice && !isInBottomSheet;
    const Tag = as;
    const showFooter = !!secondaryButtonLabel || !!primaryButtonLabel;
    function handlePrimaryButtonClick(event) {
        if (!onPrimaryButtonClick) return;
        const result = onPrimaryButtonClick(event);
        if (result instanceof Promise) {
            setLoading(true);
            result.finally(()=>setLoading(false));
        }
    }
    const sheetBody = /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        contentClassName: styles.bottomSheetContent,
        header: {
            title,
            subtitle,
            paddingBottom: 8
        },
        footer: {
            paddingTop: 8,
            // Not secondary button on purpose. Its usually a cancel button and we already have a close button.
            primaryAction: primaryButtonLabel ? {
                text: primaryButtonLabel,
                onClick: handlePrimaryButtonClick,
                disabled: primaryButtonDisabled,
                intent: primaryButtonIntent,
                testId: primaryButtonTestId !== null && primaryButtonTestId !== void 0 ? primaryButtonTestId : ConfirmationModalTestIds.primaryButton,
                loading: primaryButtonLoading || loading,
                tooltip: primaryButtonTooltip ? {
                    placement: 'top',
                    title: primaryButtonTooltip
                } : undefined,
                bi: bi ? {
                    name: bi.primaryButton.name,
                    data: bi.primaryButton.data
                } : {
                    name: ''
                }
            } : undefined
        },
        children: content
    });
    if (isInBottomSheet) return sheetBody;
    if (shouldHostInSheet) {
        return /*#__PURE__*/ _jsx(BottomSheet, {
            open: true,
            replacesHostSurface: true,
            closeSheetOnLeave: hideBottomSheetOnDismiss,
            onClose: ()=>{
                var _this;
                return (_this = onCloseButtonClick !== null && onCloseButtonClick !== void 0 ? onCloseButtonClick : onSecondaryButtonClick) === null || _this === void 0 ? void 0 : _this();
            },
            children: sheetBody
        });
    }
    return /*#__PURE__*/ _jsxs(Tag, _object_spread_props(_object_spread({
        className: cn(styles.root, className, {
            [styles.standardTextColors]: useStandardTextColors
        })
    }, rest), {
        "data-testid": testId !== null && testId !== void 0 ? testId : ConfirmationModalTestIds.root,
        style: _object_spread({
            width,
            height,
            maxHeight
        }, style),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.header, styles["".concat(skin, "Skin")], headerClassName),
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "bold",
                        variant: "h6",
                        testId: ConfirmationModalTestIds.title,
                        children: title
                    }),
                    subtitle && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "secondary",
                        testId: ConfirmationModalTestIds.subtitle,
                        children: subtitle
                    }),
                    /*#__PURE__*/ _jsx(CloseButton, _object_spread({
                        skin: "transparent",
                        onClick: onCloseButtonClick || onSecondaryButtonClick,
                        className: styles.closeButton,
                        testId: ConfirmationModalTestIds.closeButton,
                        disabled: !!cancelButtonDisabled
                    }, (bi === null || bi === void 0 ? void 0 : bi.closeButton) ? {
                        bi: {
                            name: bi.closeButton.name,
                            data: bi.closeButton.data
                        }
                    } : {
                        noBi: true
                    }))
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.body, {
                    [styles.infiniteBodyPadding]: overflowContent
                }, bodyClassName),
                children: [
                    body,
                    /*#__PURE__*/ _jsx("div", {
                        ref: contentRef,
                        "data-testid": ConfirmationModalTestIds.content,
                        className: cn(styles.content, {
                            [styles.contentAlignmentCenter]: textAlignCenter,
                            [styles.contentSmallPadding]: contentPadding === 'small',
                            [styles.mobile]: isMobileDevice
                        }, contentClassName),
                        children: typeof content === 'string' ? /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            children: content
                        }) : content
                    })
                ]
            }),
            showFooter && /*#__PURE__*/ _jsx("div", {
                className: cn(styles.footer, footerClassName),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.actions,
                    children: [
                        secondaryButtonLabel && /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                            size: "medium",
                            skin: skin === 'transparent' ? 'primaryOutline' : "".concat(skin, "Outline")
                        }, bi ? {
                            bi: {
                                name: bi.secondaryButton.name,
                                data: bi.secondaryButton.data
                            }
                        } : {
                            noBi: _noBi
                        }), {
                            onClick: onSecondaryButtonClick,
                            testId: secondaryButtonTestId !== null && secondaryButtonTestId !== void 0 ? secondaryButtonTestId : ConfirmationModalTestIds.secondaryButton,
                            loading: secondaryButtonLoading,
                            disabled: secondaryButtonDisabled,
                            type: "button",
                            children: secondaryButtonLabel
                        })),
                        primaryButtonLabel && /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                            type: "submit",
                            size: "medium",
                            form: primaryButtonFormId,
                            skin: skin === 'transparent' ? 'primary' : "".concat(skin),
                            intent: primaryButtonIntent
                        }, bi ? {
                            bi: {
                                name: bi.primaryButton.name,
                                data: bi.primaryButton.data
                            }
                        } : {
                            noBi: _noBi
                        }), {
                            onClick: handlePrimaryButtonClick,
                            testId: primaryButtonTestId !== null && primaryButtonTestId !== void 0 ? primaryButtonTestId : ConfirmationModalTestIds.primaryButton,
                            loading: primaryButtonLoading || loading,
                            disabled: primaryButtonDisabled,
                            tooltip: primaryButtonTooltip ? {
                                placement: 'top',
                                title: primaryButtonTooltip
                            } : undefined,
                            children: primaryButtonLabel
                        }))
                    ]
                })
            })
        ]
    }));
}
